import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import OBCustomCheckBoxButton from "../OBCustomCheckBoxButton";
import ExperienceOfferedShimmer from "../../shimmer_components/ExperienceOfferedShimmer";
import { useGetSupplierCategoriesQuery } from "../../../../rtk-query/slices/select-options/selectOptionsSlice";
import { useNavigate } from "react-router-dom";
import { usePostSupplierSaveExperienceMutation } from "../../../../rtk-query/slices/onboarding/experienceOfferedSlice";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { useGetUserProfileQuery } from "../../../../rtk-query/slices/profile/profileSlice";

function ExperienceOfferedTabContent({ setCurrentTab }) {
  const [initialValues, setInitialValues] = useState({
    selectedOptions: [],
  });

  const navigate = useNavigate();
  const { data: supplierCategories, isLoading: supplierCategoriesLoading } =
    useGetSupplierCategoriesQuery();
  const { data: userProfile } = useGetUserProfileQuery();
  const [
    postSupplierSaveExperience,
    { isLoading: isPostSupplierSaveExperienceLoading },
  ] = usePostSupplierSaveExperienceMutation();

  const validationSchema = Yup.object().shape({
    selectedOptions: Yup.array()
      .min(1, "At least one option must be selected")
      .required("Selection is required"),
  });

  useEffect(() => {
    if (userProfile?.data) {
      if (
        userProfile?.data?.supplier?.categories &&
        userProfile?.data?.supplier?.categories?.length > 0
      ) {
        setInitialValues({
          selectedOptions: userProfile?.data?.supplier?.categories?.map(
            (item) => {
              return item?.category_id?.toString();
            }
          ),
        });
      }
    }
  }, [userProfile]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm, setFieldError }) => {
      // Add your form submission logic here
      try {
        const data = {
          categories: values.selectedOptions,
        };
        const response = await postSupplierSaveExperience(data);

        if (response?.data?.status) {
          toast.success(response?.data?.message, {
            position: "top-center",
            autoClose: 3000,
          });
          setCurrentTab(4);
        }
        if (response?.error) {
          const errors = response?.error?.data?.errors;
          if (response?.error?.status == 401) {
            localStorage.removeItem("DODLoginToken");
            toast.error(response?.error?.data.message, {
              position: "top-center",
              autoClose: 3000,
            });
            navigate("/login");
          } else if (errors) {
            for (const field in errors) {
              if (errors.hasOwnProperty(field)) {
                const combinedErrors = errors[field].join(" ");
                if (field === "categories") {
                  setFieldError("selectedOptions", combinedErrors);
                }
              }
            }
            toast.error(response?.error?.data.message, {
              position: "top-center",
              autoClose: 3000,
            });
          } else {
            toast.error(response?.error?.data.message, {
              position: "top-center",
              autoClose: 3000,
            });
          }
        }
      } catch (error) {
        console.log("Errors:", error);
        toast.error("An unexpected error occurred. Please try again.");
      }
    },
  });

  const handleOptionChange = (e) => {
    const { value, checked } = e.target;
    if (checked) {
      formik.setFieldValue("selectedOptions", [
        ...formik.values.selectedOptions,
        value,
      ]);
    } else {
      formik.setFieldValue(
        "selectedOptions",
        formik.values.selectedOptions.filter((option) => option !== value)
      );
    }
  };

  return (
    <section className="ob-supplier-content-body">
      <div className="mt-4 ob-supplier-content-form">
        <h1 className="ob-supplier-content-heading mt-3 mb-1">
          Experiences offered
        </h1>
        <p className="ob-pi-form-description mt-0 mb-3">
          The information that you provide here will be used for your Leisure Time Out listing. Here, travelers can see what products/experiences you
          offer and where you're located.
        </p>

        {supplierCategoriesLoading ? (
          <ExperienceOfferedShimmer />
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <p className="ob-pi-label m-0">
              <span className="text-danger ps-1">*</span>
              What's the main type of product you offer?
            </p>
            <p className="ob-pi-form-description mt-0 mb-4">
              This will help us categorize your supplier listing on our site.
              You’ll be able to create products in any category, no matter which
              option you select here.
            </p>
            {supplierCategories?.data?.map((category) => (
              <OBCustomCheckBoxButton
                key={category?.category_id}
                id={category?.category_id}
                name="options"
                value={category?.category_id.toString()}
                checked={formik.values.selectedOptions.includes(
                  category?.category_id.toString()
                )}
                onChange={handleOptionChange}
                icon={category?.category_icon} // Example icon
                text1={category?.category_name}
                text2={category?.key_information}
                tooltipText="Choose one out of all these option that best represent your offering "
              />
            ))}

            {formik.touched.selectedOptions && formik.errors.selectedOptions ? (
              <div className="error-text invalid-feedback d-flex">
                {formik.errors.selectedOptions}
              </div>
            ) : null}

            <div className="d-flex justify-content-between mt-5">
              <button
                disabled={isPostSupplierSaveExperienceLoading}
                type="button"
                className="back-btn"
                onClick={() => setCurrentTab(2)}
              >
                Back
              </button>
              <button
                disabled={isPostSupplierSaveExperienceLoading}
                type="submit"
                className="submit-btn"
              >
                Save & Continue{" "}
                {isPostSupplierSaveExperienceLoading && <Spinner size="sm" />}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

export default ExperienceOfferedTabContent;
